import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import userflow from 'userflow.js';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import ProgressWizard from 'components/field/ProgressWizard';
import PageHeader from 'components/global/PageHeader';
import { PageHeaderActions, PageHeaderContent } from 'components/global/styled/PageHeader.styled';

import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';
import { useBlocker } from 'hooks/usePrompt';

import BulletDeliveryInstructions from 'pages/PriceCalculator/BulletDeliveryInstructions/BulletDeliveryInstructions';
import LogisticDetailsForImports from 'pages/PriceCalculator/LogisticDetailsForImports/LogisticDetailsForImports';

import { Breakpoints } from 'utils/const.styles';
import PATHS, { mergePaths } from 'utils/paths';

import { faBoxCheck, faBuilding, faMapPin, faUserTag } from '@fortawesome/pro-solid-svg-icons';

import * as S from './PriceCalculator.styled';
import QuickShipment from './QuickShipments/QuickShipment';
import ShippingDetailsAndOptionsTab from './ShippingDetailsAndOptionsTab';
import ShippingDocumentsAndDeliveryOptionsTab from './ShippingDocumentsAndDeliveryOptionsTab';
import ShippingSuccessTab from './ShippingSuccessTab';

const PriceCalculator = ({ type }) => {
	const { state: locationState } = useLocation();
	const [searchParams] = useSearchParams();
	const referredOrderID = searchParams.get('orderID') || locationState?.orderID;

	const navigate = useNavigate();
	const { getTimeWithTimeZone, Country, Currency } = useLocale();
	const { t } = useTranslation();
	const [disabledSelectOrder, setDisabledSelectOrder] = useState(!!referredOrderID);
	const [isContinuesAction, setIsContinuesAction] = useState(!!referredOrderID);

	const [isInternationalToQatar, setIsInternationalToQatar] = useState(false);
	const [hasOptionalPickup, setHasOptionalPickup] = useState(false);
	const [noticeData, setNoticeData] = useState(null);
	const [dcCompanyData, setDcCompanyData] = useState(null);
	const [isAggregatorBulletDelivery, setIsAggregatorBulletDelivery] = useState(false);
	const [isLockerDelivery, setIsLockerDelivery] = useState(false);
	const isDesktop = useMediaQuery(Breakpoints.xldesktop);
	const warehouseCheck = useSelector((state) => state.instruction?.warehouseCheck);
	const [pickupLocationVisible, setPickupLocationVisible] = useState(!warehouseCheck);
	const [pickupType, setPickupType] = useState('otoRates');
	const [pickupLocationCreated, setPickupLocationCreated] = useState(false);
	const [formMode, setFormMode] = useState('add');
	const nameRegex = /^[a-zA-Z0-9-]+$/;
	const user = useSelector((state) => state.user);
	const isImportOrder = type === 'import';
	const isQuickShipment = type === 'quickShipment';
	const [tab, setTab] = useState(0);

	const defaultBoxState = useSelector((state) => state.user.defaultBox);
	const defaultBox = defaultBoxState
		? {
				ID: defaultBoxState.id,
				width: defaultBoxState.width,
				height: defaultBoxState.height,
				length: defaultBoxState.length,
				name: defaultBoxState.boxName,
				weight: 0
			}
		: { width: 10, height: 10, length: 10, weight: 0 };

	const schema = useMemo(() => {
		if (tab === 1 && isImportOrder) {
			return yup.object().shape({
				trackingURL: yup.string().url().nullable().required(),
				supplierURL: yup.string().url().nullable().required(),
				trackingNumber: yup.string().required()
			});
		}

		if (isQuickShipment) {
			if (pickupLocationVisible && tab === 0) {
				return yup.object().shape({
					senderAddress: yup.object().shape({
						text: yup.string().nullable().required(),
						countryName: yup.string().isCountryAllowed().nullable().required(),
						stateName: yup.string().nullable(),
						cityName: yup.string().nullable().required(),
						districtName: yup.string().nullable(),
						street: yup.string().nullable(),
						buildingName: yup.string().nullable(),
						zipCode: yup.string().nullable(),
						lat: yup.number().min(-90).max(90).nullable(),
						lng: yup.number().min(-180).max(180).nullable(),
						addressInputType: yup.string().nullable()
					}),
					senderEmail: yup.string().email().nullable().required(),
					senderMobileNumber: yup.string().nullable().phoneNumber().required(),
					senderName: yup.string().nullable().required(),
					warehouseName: yup.string().nullable().min(3).trim().required()
				});
			} else if (pickupLocationVisible ? tab === 1 : tab === 0) {
				return yup.object().shape({
					fullName: yup.string().nullable().required(),
					email: yup.string().email().nullable(),
					phone: yup.string().nullable().required().phoneNumber(),
					address: yup.object().shape({
						shortAddressCode: yup.string().nullable(),
						text: yup.string().nullable().required(),
						countryName: yup.string().nullable().required(),
						stateName: yup.string().nullable(),
						cityName: yup.string().nullable().required(),
						districtName: yup.string().nullable(),
						buildingName: yup.string().nullable(),
						street: yup.string().nullable(),
						lat: yup.number().min(-90).max(90).nullable(),
						lng: yup.number().min(-180).max(180).nullable(),
						zipCode: yup.string().nullable().requiredZipCode(),
						addressInputType: yup.string().nullable()
					})
				});
			} else if (pickupLocationVisible ? tab === 2 : tab === 1) {
				return yup.object().shape({
					otoID: yup
						.string()
						.nullable()
						.matches(nameRegex, t('errorMsg.onlyEnglishLetters'))
						.min(3)
						.trim()
						.max(50)
						.required(),
					orderComments: yup
						.string()
						.nullable()
						.when('products', {
							is: (products) => products?.length > 0,
							then: (schema) => schema,
							otherwise: (schema) => schema.required()
						}),
					products: yup
						.array()
						.of(
							yup.object().shape({
								productID: yup.number().nullable(),
								name: yup.string().nullable().required(),
								quantity: yup.number().min(1).nullable(),
								price: yup.number().min(0).nullable(),
								tax: yup.number().nullable(),
								total: yup.number().nullable(),
								SKU: yup.string().nullable(),
								hsCode: yup.string().nullable().hsCodeLenght().hsCode()
							})
						)
						.when('isInternationalOrder', {
							is: true,
							then: (schema) =>
								schema.test({
									test: (value) => value.length > 0,
									name: 'internationalOrderProductRequired',
									message: 'errorMsg.internationalOrderProductRequired'
								}),
							otherwise: (schema) => schema
						}),
					paymentMethod: yup.string().nullable().required(),
					grandTotal: yup.number().nullable().min(1).required(),
					boxes: yup.array().of(
						yup.object().shape({
							ID: yup.number().nullable(),
							width: yup.number().positive().nullable().required(),
							height: yup.number().positive().nullable().required(),
							length: yup.number().positive().nullable().required(),
							weight: yup.number().positive().nullable().required()
						})
					),
					warehouseID: yup.number().nullable().required(),
					currency: yup.string().nullable().required().customOrderCurrencyValidation(),
					totalDue: yup
						.number()
						.nullable()
						.when('paymentMethod', {
							is: (value) => value === 'cod',
							then: (schema) => schema.required().min(1)
						}),
					weight: yup.number().min(0.01).nullable().required(),
					packageCount: yup.number().min(1).nullable().required()
				});
			}
		}

		if (isAggregatorBulletDelivery && (isQuickShipment ? tab === (pickupLocationVisible ? 4 : 3) : tab === 1)) {
			return yup.object().shape({
				priceRange: yup
					.array()
					.of(yup.number().nullable().required())
					.when(['priceConfig'], ([priceConfig], schema) => {
						const { price, maxPrice } = priceConfig || {};

						if (price && maxPrice) {
							return schema.test({
								test: (value) => {
									const [, val] = value;
									return val >= price && val <= maxPrice;
								},
								message: t('priceRangeError')
							});
						}

						return schema;
					}),
				timeRange: yup.array().of(yup.number().nullable()),
				confirmation: yup
					.boolean()
					.test('requiredConfirmBulletDelivery', t('confirmBulletDelivery'), (value, context) => {
						const { price, maxPrice } = context.parent.priceConfig ?? {};
						return price === maxPrice ? true : value;
					}),
				timeSlots: yup
					.array()
					.of(yup.string().nullable().required())
					.test('valid-timeSlots', t('timeSlotError'), (timeSlots) => {
						// if there is only one time slot, and it is less than now time, this should be an error
						const startTimeFirstSlot = timeSlots[0]?.split(' - ')[0];
						const now = getTimeWithTimeZone(dayjs());
						return !(timeSlots?.length === 1 && dayjs(startTimeFirstSlot).isBefore(now));
					})
					.when(['priceConfig'], ([priceConfig], schema) => {
						// bullet cut off time should be less than now time
						const { bulletCutOff } = priceConfig || {};
						const now = getTimeWithTimeZone(dayjs());
						return dayjs(bulletCutOff).isBefore(now)
							? schema.required().test('is-valid', t('timeSlotError'), () => false)
							: schema;
					})
			});
		}

		return yup.object().shape({
			orderID: yup.number().nullable(),
			originAddress: yup
				.object()
				.shape({
					text: yup.string().nullable().required(),
					cityName: yup
						.string()
						.nullable()
						.when([], {
							is: () => isImportOrder,
							then: (schema) => schema,
							otherwise: (schema) => schema.required()
						}),
					countryName: yup.string().nullable().required(),
					lat: yup.number().nullable(),
					lng: yup.number().nullable()
				})
				.required(),
			destinationAddress: yup
				.object()
				.shape({
					text: yup.string().nullable().required(),
					cityName: yup
						.string()
						.nullable()
						.when([], {
							is: () => isImportOrder,
							then: (schema) => schema,
							otherwise: (schema) => schema.required()
						}),
					countryName: yup.string().nullable().required(),
					lat: yup.number().nullable(),
					lng: yup.number().nullable()
				})
				.required(),
			boxes: yup.array().of(
				yup.object().shape({
					ID: yup.number().nullable(),
					width: yup.number().positive().nullable().required(),
					height: yup.number().positive().nullable().required(),
					length: yup.number().positive().nullable().required(),
					weight: yup.number().positive().nullable().required()
				})
			)
		});
	}, [isAggregatorBulletDelivery, isImportOrder, tab, t, isQuickShipment, pickupLocationVisible]);

	const defaultValues = {
		editMode: false,
		dataLoading: false,
		orderID: referredOrderID ?? null,
		destinationAddress: null,
		originAddress: null,
		frontSideIDCard: null,
		backSideIDCard: null,
		boxes: [defaultBox],
		...(isAggregatorBulletDelivery
			? { priceRange: [0, 0], timeRange: [0, 0], confirmation: false, timeSlots: [] }
			: {}),
		...(isQuickShipment
			? {
					fullName: null,
					email: null,
					phone: null,
					address: {
						text: null,
						countryName: Country,
						stateName: null,
						cityName: null,
						districtName: null,
						buildingName: null,
						street: null,
						lat: null,
						lng: null,
						zipCode: null
					},
					orderDate: dayjs().toISOString(),
					orderComments: null,
					warehouseID: user?.generalSettings?.defaultWarehouse,
					products: [],
					paymentMethod: 'paid',
					currency: Currency,
					grandTotal: null,
					totalDue: null,
					weight: 0,
					packageCount: 1,
					manuallyAdded: true,
					reloadPrices: true,
					isInternationalOrder: false,
					senderAddress: {
						text: null,
						countryName: Country,
						stateName: null,
						cityName: null,
						districtName: null,
						street: null,
						buildingName: null,
						zipCode: null,
						lat: null,
						lng: null
					},
					senderEmail: user.senderEmail,
					senderName: user.fullName,
					senderMobileNumber: user.phone,
					warehouseName: 'DefaultWH',
					otoID: null
				}
			: {})
	};
	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues
	});

	const { getValues, reset, setValue, watch, setError, setFocus } = formMethods;
	const priceConfig = watch('priceConfig');
	const orderID = watch('orderID');

	const nextStep = () => {
		setTab(tab + 1);
	};

	const prevStep = () => {
		if (tab > 0) {
			setTab(tab - 1);
		}
	};

	const resetStep = () => {
		setTab(0);
		setError('address.cityName', {
			type: 'manual',
			message: 'price-calculator.city_not_found.title'
		});
		setTimeout(() => {
			setFocus('address.cityName');
		}, 300);
	};

	const generateWizardSection = useCallback(() => {
		let message = '';

		if (isImportOrder) {
			message = t('logisticDetails');
		} else if (isAggregatorBulletDelivery) {
			message = t('instruction');
		} else if (isInternationalToQatar && hasOptionalPickup) {
			message = `${t('uploadDocuments')} ${t('&')} ${t('selectPickupOptions')}`;
		} else if (isInternationalToQatar) {
			message = t('uploadDocuments');
		} else if (hasOptionalPickup) {
			message = t('selectPickupOptions');
		} else if (!!noticeData && !isInternationalToQatar) {
			message = t('instruction');
		}

		return message ? [{ name: message }] : [];
	}, [isImportOrder, isAggregatorBulletDelivery, hasOptionalPickup, isInternationalToQatar, noticeData, t]);

	const getQuickShipmentStages = useMemo(() => {
		const quickShipmentTabs =
			pickupLocationVisible === false
				? [
						{ name: 'customerDetails', icon: faUserTag },
						{ name: 'orderDetails', icon: faBoxCheck }
					]
				: [
						{ name: 'pickupLocation', icon: faMapPin },
						{ name: 'customerDetails', icon: faUserTag },
						{ name: 'orderDetails', icon: faBoxCheck }
					];

		pickupType === 'otoRates' && quickShipmentTabs.push({ name: 'selectDeliveryCompany', icon: faBuilding });
		return quickShipmentTabs;
	}, [pickupLocationVisible, pickupType]);

	const wizardStages = useMemo(
		() => [
			{ name: 'shippingDetailsandOptions' },
			...generateWizardSection(),
			{ name: 'shippingSuccess', success: true }
		],
		[
			hasOptionalPickup,
			isInternationalToQatar,
			noticeData,
			generateWizardSection,
			isImportOrder,
			isAggregatorBulletDelivery
		]
	);

	const resetForm = () => {
		setTab(0);
		reset(defaultValues);
		setDisabledSelectOrder(false);
		setIsContinuesAction(false);
		setIsInternationalToQatar(false);
		setIsAggregatorBulletDelivery(false);
		setHasOptionalPickup(false);
		setNoticeData(null);
		setDcCompanyData(null);
		setFormMode('add');
	};

	useEffect(() => {
		if (locationState?.resetState) {
			resetForm();
			navigate(
				isImportOrder
					? mergePaths(PATHS.IMPORT_ORDERS, PATHS.IMPORT_CALCULATOR)
					: isQuickShipment
						? mergePaths(PATHS.CREATE_SHIPMENT)
						: mergePaths(PATHS.SHIPMENTS, PATHS.PRICE_CALCULATOR),
				{
					state: {
						orderID: referredOrderID,
						external: locationState?.external
					},
					replace: true
				}
			);
		} else {
			const orderID = getValues('orderID');
			if (orderID !== referredOrderID) {
				setValue('orderID', referredOrderID ?? null);
			}
			setDisabledSelectOrder(!!referredOrderID);
			setIsContinuesAction(!!referredOrderID);
		}
	}, [locationState, referredOrderID]);

	const handleCreateNewShipment = () => {
		setPickupLocationVisible(false);
		resetForm();

		const navigateToPath = locationState?.external
			? -1
			: isImportOrder
				? PATHS.IMPORT_CALCULATOR
				: isQuickShipment
					? PATHS.CREATE_SHIPMENT
					: PATHS.PRICE_CALCULATOR;

		navigate(navigateToPath, {
			replace: true
		});
	};

	useBlocker(
		async (tx) => {
			userflow.start('096471d5-c9d8-4dd2-9acb-e2c0c16c7077');
			tx.retry();
		},
		userflow.isIdentified() && !watch('shipmentResultData.shipmentId') && dcCompanyData?.length > 0
	);

	const headerProps = useMemo(() => {
		const isExternal = locationState?.external;
		if (isQuickShipment) {
			return { title: 'createShipment', tooltip: 'priceCalculatorPageHeaderTooltip' };
		}
		const title = isExternal
			? isImportOrder
				? 'withOtoRates'
				: 'shipments'
			: isImportOrder
				? 'getEstimate'
				: 'priceCalculator';
		const tooltip = isExternal || isImportOrder ? undefined : 'priceCalculatorPageHeaderTooltip';

		return { title, tooltip };
	}, [isImportOrder, locationState, t]);

	const getIndex = () => {
		if (isQuickShipment) {
			if (
				(hasOptionalPickup || isInternationalToQatar || !!noticeData || isAggregatorBulletDelivery) &&
				tab > (pickupLocationVisible ? 3 : 2)
			) {
				return tab - 1;
			}
		}
		return tab;
	};

	return (
		<div>
			<S.PriceCalculatorContainer>
				<PageHeader
					lordIcon={
						isQuickShipment
							? 'https://storage.googleapis.com/tryoto-public/lottie-files-compressed/shipments-icon-new.json.gz'
							: isImportOrder
								? 'https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/get_estimate.json.gz'
								: 'https://storage.googleapis.com/tryoto-public/lottie-files-compressed/calculator-icon-new.json.gz'
					}
					{...(!isQuickShipment && { moduleName: 'aggregator-shipping' })}
					title={headerProps.title}
					tooltip={headerProps.tooltip}
					showBackButton={locationState?.external}>
					{(((!pickupLocationVisible || isDesktop) && isQuickShipment) || !isQuickShipment) && (
						<PageHeaderContent>
							<ProgressWizard
								clickable={false}
								stages={isQuickShipment ? getQuickShipmentStages : wizardStages}
								setIndex={setTab}
								index={getIndex()}
							/>
						</PageHeaderContent>
					)}
					<PageHeaderActions className="tablet-hidden" />
				</PageHeader>
				{pickupLocationVisible && !isDesktop && isQuickShipment && (
					<ProgressWizard
						className="bottomProgress"
						clickable={false}
						stages={isQuickShipment ? getQuickShipmentStages : wizardStages}
						setIndex={setTab}
						index={getIndex()}
					/>
				)}
				<S.StyledPriceCalculatorForm formMethods={formMethods} borderless className="p-0">
					{isQuickShipment && (pickupLocationVisible ? tab <= 2 : tab <= 1) ? (
						<QuickShipment
							nextStep={nextStep}
							tab={tab}
							prevStep={prevStep}
							pickupLocationVisible={pickupLocationVisible}
							pickupType={pickupType}
							setPickupType={setPickupType}
							formMode={formMode}
							setFormMode={setFormMode}
							pickupLocationCreated={pickupLocationCreated}
							setPickupLocationCreated={setPickupLocationCreated}
						/>
					) : null}
					{(isQuickShipment ? tab === (pickupLocationVisible ? 3 : 2) : tab === 0) && (
						<ShippingDetailsAndOptionsTab
							nextStep={nextStep}
							isContinuesAction={isContinuesAction}
							setIsContinuesAction={setIsContinuesAction}
							disabledSelectOrder={disabledSelectOrder}
							setHasOptionalPickup={setHasOptionalPickup}
							isInternationalToQatar={isInternationalToQatar}
							setIsInternationalToQatar={setIsInternationalToQatar}
							isAggregatorBulletDelivery={isAggregatorBulletDelivery}
							setIsAggregatorBulletDelivery={setIsAggregatorBulletDelivery}
							setIsLockerDelivery={setIsLockerDelivery}
							dcCompanyData={dcCompanyData}
							setDcCompanyData={setDcCompanyData}
							showBackButton={locationState?.external}
							setNoticeData={setNoticeData}
							isImportOrder={isImportOrder}
							isQuickShipment={isQuickShipment}
							prevStep={prevStep}
							resetStep={resetStep}
						/>
					)}

					{!isAggregatorBulletDelivery && !isImportOrder
						? (hasOptionalPickup || isInternationalToQatar || !!noticeData) &&
							(isQuickShipment ? tab === (pickupLocationVisible ? 4 : 3) : tab === 1) && (
								<ShippingDocumentsAndDeliveryOptionsTab
									isInternationalToQatar={isInternationalToQatar}
									hasOptionalPickup={hasOptionalPickup}
									setHasOptionalPickup={setHasOptionalPickup}
									prevStep={prevStep}
									nextStep={nextStep}
									external={locationState?.external}
									noticeData={noticeData}
									isLockerDelivery={isLockerDelivery}
									isQuickShipment={isQuickShipment}
								/>
							)
						: (isAggregatorBulletDelivery &&
								(isQuickShipment ? tab === (pickupLocationVisible ? 4 : 3) : tab === 1) && (
									<BulletDeliveryInstructions
										nextStep={nextStep}
										prevStep={prevStep}
										setAggregatorBulletDelivery={setIsAggregatorBulletDelivery}
										priceConfig={priceConfig}
										orderID={orderID}
										isQuickShipment={isQuickShipment}
									/>
								)) ||
							(tab === 1 && isImportOrder && !isQuickShipment && (
								<LogisticDetailsForImports nextStep={nextStep} prevStep={prevStep} />
							))}

					{isAggregatorBulletDelivery &&
						(isQuickShipment ? tab === (pickupLocationVisible ? 5 : 4) : tab === 2) && (
							<ShippingSuccessTab
								handleCreateNewShipment={handleCreateNewShipment}
								isAggregatorBulletDelivery
							/>
						)}
					{isImportOrder && !isQuickShipment && tab === 2 && (
						<ShippingSuccessTab
							handleCreateNewShipment={handleCreateNewShipment}
							importOrder={isImportOrder}
						/>
					)}

					{!isAggregatorBulletDelivery &&
						!isImportOrder &&
						((!hasOptionalPickup &&
							!isInternationalToQatar &&
							!noticeData &&
							(isQuickShipment ? tab === (pickupLocationVisible ? 4 : 3) : tab === 1)) ||
							((hasOptionalPickup || isInternationalToQatar || !!noticeData) &&
								(isQuickShipment ? tab === (pickupLocationVisible ? 5 : 4) : tab === 2))) && (
							<ShippingSuccessTab handleCreateNewShipment={handleCreateNewShipment} />
						)}
				</S.StyledPriceCalculatorForm>
			</S.PriceCalculatorContainer>
		</div>
	);
};

export default PriceCalculator;
